import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workService } from '../../services/workService';
import { Goal, Project, ACCENT_CLASSES, AccentColor, GOAL_STATUSES, PROJECT_STATUSES, TASK_STATUSES, PRIORITIES } from '../../types/work';
import { FiX } from 'react-icons/fi';

type EntityType = 'goal' | 'project' | 'task';

interface Props {
  entityType: EntityType;
  onClose: () => void;
  /** Pre-fill goal_id for new projects, project_id/goal_id for new tasks */
  defaults?: { goal_id?: string; project_id?: string };
  /** Edit mode: pre-fill from existing item */
  mode?: 'create' | 'edit';
  initialData?: { id: string; title?: string; description?: string; priority?: string; status?: string; due_date?: string; goal_id?: string; project_id?: string };
}

const ENTITY_CONFIG: Record<EntityType, { label: string; accent: AccentColor; icon: string; statuses: readonly string[]; defaultStatus: string }> = {
  goal:    { label: 'Goal',    accent: 'emerald', icon: '🎯', statuses: GOAL_STATUSES,    defaultStatus: 'active' },
  project: { label: 'Project', accent: 'teal',    icon: '📁', statuses: PROJECT_STATUSES, defaultStatus: 'active' },
  task:    { label: 'Task',    accent: 'violet',   icon: '✓',  statuses: TASK_STATUSES,    defaultStatus: 'todo' },
};

const CreateWorkItemModal: React.FC<Props> = ({ entityType, onClose, defaults, mode = 'create', initialData }) => {
  const { darkMode } = useTheme();
  const queryClient = useQueryClient();
  const config = ENTITY_CONFIG[entityType];
  const colors = ACCENT_CLASSES[config.accent];
  const isEdit = mode === 'edit';

  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [priority, setPriority] = useState(initialData?.priority || 'medium');
  const [status, setStatus] = useState(initialData?.status || config.defaultStatus);
  const [dueDate, setDueDate] = useState(initialData?.due_date ? initialData.due_date.slice(0, 10) : '');
  const [goalId, setGoalId] = useState(initialData?.goal_id || defaults?.goal_id || '');
  const [projectId, setProjectId] = useState(initialData?.project_id || defaults?.project_id || '');
  const [error, setError] = useState<string | null>(null);

  // Entity pickers — fetch goals and projects for dropdowns
  const { data: goals = [] } = useQuery(['goals-picker'], () => workService.getGoals({ limit: 100 }), {
    enabled: entityType === 'project' || entityType === 'task',
  });
  const { data: projects = [] } = useQuery(
    ['projects-picker', goalId],
    () => workService.getProjects(goalId ? { goal_id: goalId } : { limit: 100 }),
    { enabled: entityType === 'task' },
  );

  const mutation = useMutation(
    async () => {
      const data: any = { title, description: description || undefined, priority, status };
      if (dueDate) data.due_date = new Date(dueDate).toISOString();
      if (entityType === 'project' && goalId) data.goal_id = goalId;
      if (entityType === 'task') {
        if (goalId) data.goal_id = goalId;
        if (projectId) data.project_id = projectId;
      }

      if (isEdit && initialData?.id) {
        if (entityType === 'goal') return workService.updateGoal(initialData.id, data);
        if (entityType === 'project') return workService.updateProject(initialData.id, data);
        return workService.updateTask(initialData.id, data);
      } else {
        if (entityType === 'goal') return workService.createGoal(data);
        if (entityType === 'project') return workService.createProject(data);
        return workService.createTask(data);
      }
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries
        const key = entityType === 'goal' ? 'goals' : entityType === 'project' ? 'projects' : 'tasks';
        queryClient.invalidateQueries([key]);
        if (isEdit && initialData?.id) {
          queryClient.invalidateQueries([entityType === 'goal' ? 'goal' : entityType === 'project' ? 'project' : 'task', initialData.id]);
        }
        // Invalidate parent queries for count updates
        if (entityType === 'project' && goalId) {
          queryClient.invalidateQueries(['goals']);
          queryClient.invalidateQueries(['goal', goalId]);
        }
        if (entityType === 'task' && projectId) {
          queryClient.invalidateQueries(['projects']);
          queryClient.invalidateQueries(['project', projectId]);
          queryClient.invalidateQueries(['project-tasks', projectId]);
        }
        onClose();
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || err.message || 'Operation failed');
      },
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setError(null);
    mutation.mutate();
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
  } focus:outline-none focus:ring-1 focus:ring-gray-500/30`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className={`w-full max-w-lg rounded-xl shadow-2xl ${darkMode ? 'bg-gray-900' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b border-l-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${colors.border}`}>
          <div className="flex items-center gap-2">
            <span className="text-lg">{config.icon}</span>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {isEdit ? 'Edit' : 'New'} {config.label}
            </h2>
          </div>
          <button onClick={onClose} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title *</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`Enter ${config.label.toLowerCase()} title...`} autoFocus className={inputClass} />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description..." rows={3} className={`${inputClass} resize-none`} />
          </div>

          {/* Entity pickers */}
          {entityType === 'project' && (
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Goal (optional)</label>
              <select value={goalId} onChange={(e) => setGoalId(e.target.value)} className={inputClass}>
                <option value="">No goal (standalone)</option>
                {goals.map((g: Goal) => <option key={g.id} value={g.id}>{g.icon || '🎯'} {g.title}</option>)}
              </select>
            </div>
          )}

          {entityType === 'task' && (
            <>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Goal (optional)</label>
                <select value={goalId} onChange={(e) => { setGoalId(e.target.value); setProjectId(''); }} className={inputClass}>
                  <option value="">No goal</option>
                  {goals.map((g: Goal) => <option key={g.id} value={g.id}>{g.icon || '🎯'} {g.title}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Project (optional)</label>
                <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={inputClass}>
                  <option value="">No project (standalone)</option>
                  {projects.map((p: Project) => <option key={p.id} value={p.id}>📁 {p.title}</option>)}
                </select>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                {config.statuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Due Date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r ${config.accent === 'emerald' ? 'from-emerald-600 to-teal-500' : config.accent === 'teal' ? 'from-teal-600 to-cyan-500' : 'from-violet-600 to-purple-500'} hover:shadow-lg transition-shadow disabled:opacity-50`}
            >
              {mutation.isLoading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? `Save ${config.label}` : `Create ${config.label}`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkItemModal;
